/**
 * Video lessons (SPEC §38.2, decision 27): one recorded walkthrough whose steps are chapters, each with the offset its
 * caption appeared at (`startMs`, from the recorder's sidecar). The player turns them into step navigation and keeps
 * the transcript in step with the video. Pure: unit-tested in chapterModel.test.ts.
 */

/** Chapter starts in ms, or null when the steps are not chapters of one video (any missing or out of order). */
export function chaptersOf(steps: readonly { startMs: number | null }[]): number[] | null {
  if (!steps.length) return null;
  const starts: number[] = [];
  for (const s of steps) {
    if (s.startMs === null || !Number.isFinite(s.startMs) || s.startMs < 0) return null;
    if (starts.length && s.startMs < starts[starts.length - 1]!) return null;
    starts.push(s.startMs);
  }
  return starts;
}

/** The chapter playing at `timeMs`: the last one that started (the first before any started), with 1 ms of tolerance for seeks. */
export function chapterAt(chapters: readonly number[], timeMs: number): number {
  let index = 0;
  for (let i = 0; i < chapters.length; i++) if (timeMs + 1 >= chapters[i]!) index = i;
  return index;
}

/** Where the video seeks to for a chapter, in seconds (clamped to the chapters there are). */
export function seekSecondsOf(chapters: readonly number[], index: number): number {
  if (!chapters.length) return 0;
  const i = Math.min(chapters.length - 1, Math.max(0, Math.trunc(index) || 0));
  return chapters[i]! / 1000;
}

/** The chapter a ←/→ key moves to from the current one, or null at either end. */
export function stepChapter(chapters: readonly number[], current: number, delta: 1 | -1): number | null {
  const next = current + delta;
  return next >= 0 && next < chapters.length ? next : null;
}
